import { OneOrManySchema } from 'notable-common';
import { z } from 'zod';
import { FlowRunState, TERMINAL_FLOW_RUN_STATES } from './flowRunState.js';
import { FlowRunType } from './runType.js';
import { FlowReRunVersionTarget } from '../flow/versionTarget.js';
import { FlowDefinitionState } from '../flowDefinition/flowDefinition.js';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/**
 * How many run ids one request may name.
 *
 * High because the bulk endpoints address a whole selection by id, and low enough that the `IN`
 * clause it becomes stays within what Postgres will plan.
 */
export const MAX_FLOW_RUN_IDS_IN_QUERY = 10_000;
/**
 * How a caller selects flow runs: the filters `GET /flow-runs` accepts, and the selection the bulk
 * re-run and cancel endpoints act on.
 *
 * Offset-paged and unbounded in time, unlike the cursor-paged v2 list. Prefer that one for reading;
 * this stays because the bulk endpoints select with it.
 */
export const FlowRunFindAllQuerySchema = z.object({
    id: OneOrManySchema(z.uuid(), { arrayMax: MAX_FLOW_RUN_IDS_IN_QUERY })
        .describe('Return only these runs.')
        .optional(),
    flowId: z.uuid().describe('Return only runs of this flow.').optional(),
    flowDefinitionId: z.uuid().describe('Return only runs of this flow definition.').optional(),
    limit: FlowApiPageLimitSchema.describe('How many runs to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    /**
     * Coerced rather than stated as ISO-8601: the runs table filters by plain date, the export jobs
     * persist an instant, and the stored selection is read back through this same schema.
     */
    createdBefore: z.coerce.date().optional(),
    createdAfter: z.coerce.date().optional(),
    startedBefore: z.coerce.date().optional(),
    startedAfter: z.coerce.date().optional(),
    endedBefore: z.coerce.date().optional(),
    endedAfter: z.coerce.date().optional(),
    startedByEmail: z.string().describe('Return only runs started by this user.').optional(),
    organizationId: OneOrManySchema(z.uuid())
        .describe('Return only runs in this organization, or in any of these organizations.')
        .optional(),
    orderBy: z.string().describe('Column to order by, such as created.').optional(),
    orderDirection: z.enum(['asc', 'desc']).describe('Direction to order by.').optional(),
    name: z.string().describe('Return only runs of a flow with this name.').optional(),
    state: OneOrManySchema(z.enum(FlowRunState))
        .describe('Return only runs in this state, or in any of these states.')
        .optional(),
    flowDefinitionState: OneOrManySchema(z.enum(FlowDefinitionState))
        .describe('Return only runs whose definition is in this lifecycle state.')
        .optional(),
    type: z.enum(FlowRunType).describe('Return only runs of this type, such as Test.').optional(),
    groupId: z.uuid().describe('Return only runs of this bulk or re-run group.').optional(),
    parentId: z.uuid().describe('Return only runs started by this parent run.').optional(),
    parentFlowDefinitionBlockId: z
        .uuid()
        .describe('Return only runs started by this subflow block.')
        .optional(),
    version: OneOrManySchema(z.string())
        .describe('Return only runs of this flow definition version.')
        .optional(),
    fleetId: z.uuid().describe('Return only runs carried out by this fleet.').optional(),
    entityType: z.string().describe('Return only runs about this type of entity.').optional(),
    entityId: z.string().describe('Return only runs about this entity.').optional(),
});
/**
 * Body `POST /re-run-bulk` accepts: the runs to re-run, plus which definition to re-run them
 * against. Narrower than the query it builds on — a run in flight has nothing to re-run, and every
 * re-run belongs to one flow.
 */
export const FlowReRunBulkBodySchema = FlowRunFindAllQuerySchema.omit({
    flowId: true,
    state: true,
}).extend({
    flowId: z.uuid().describe('Re-run runs of this flow.'),
    state: OneOrManySchema(z.enum(TERMINAL_FLOW_RUN_STATES))
        .describe('Re-run runs that ended in this state. Defaults to every finished state.')
        .default([...TERMINAL_FLOW_RUN_STATES]),
    versionTarget: z
        .enum(FlowReRunVersionTarget)
        .describe('Which definition to re-run against, such as the one each original run used.'),
});
/** Body `POST /cancel-bulk` accepts: every run the query selects is cancelled. */
export const FlowCancelBulkBodySchema = FlowRunFindAllQuerySchema.omit({
    organizationId: true,
}).extend({
    // TODO: Once `flowRunIds` is gone, make this required: a cancellation is scoped to one organization.
    organizationId: z.uuid().describe('Organization whose runs are cancelled.').optional(),
    /** @deprecated Select the runs with `id` instead. */
    flowRunIds: z.array(z.uuid()).max(MAX_FLOW_RUN_IDS_IN_QUERY).optional(),
});
//# sourceMappingURL=findAll.js.map